import React, { useCallback, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useOutsideClick } from '../../hooks/useOutsideClick';
export const TODAY = new Date(2026, 7, 6);
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Field shows M/D/YYYY; the review table shows DD/MM/YYYY, matching the live app. */
export function toReviewDate(value: string): string {
  if (!value) return '';
  const [month, day, year] = value.split('/');
  if (!month || !day || !year) return value;
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
}
interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
}
export function DatePicker({
  value,
  onChange,
  error
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(TODAY.getMonth());
  const [year, setYear] = useState(TODAY.getFullYear());
  const close = useCallback(() => setOpen(false), []);
  const ref = useOutsideClick<HTMLDivElement>(close);
  const gridStart = new Date(year, month, 1);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const weeks: Date[][] = [];
  for (let w = 0; w < 6; w += 1) {
    const week = Array.from({
      length: 7
    }, (_, d) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + w * 7 + d);
      return date;
    });
    if (w < 5 || week.some(date => date.getMonth() === month)) weeks.push(week);
  }
  function step(delta: number) {
    const next = new Date(year, month + delta, 1);
    setMonth(next.getMonth());
    setYear(next.getFullYear());
  }
  return <div ref={ref} className="relative" data-id="element-304" data-mp="setxt8uzgqid">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className={`flex h-9 w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 text-left text-[13px] ${error ? 'border-red-400' : open ? 'border-brand-500' : 'border-ink-200'} ${value ? 'text-ink-800' : 'text-ink-400'}`} data-id="element-305" data-mp="oeicdtusqm5u">
        <span data-id="element-306" data-mp="4ut9jov2xkc1">{value || 'Select Date of Issue'}</span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-ink-500" data-id="element-307" data-mp="9r1l6sa2iveg" />
      </button>

      {open && <div className="absolute left-0 top-11 z-40 w-[184px] rounded-lg border border-ink-200 bg-white p-2.5 shadow-pop" data-id="element-308" data-mp="xh5o0xffmr4e">
          <div className="flex items-center justify-between" data-id="element-309" data-mp="11ihfz9ls3ofr">
            <button type="button" onClick={() => step(-1)} aria-label="Previous month" className="rounded p-1 text-ink-500 hover:bg-ink-100" data-id="element-310" data-mp="ns43qv1utnkm">
              <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-311" data-mp="1gf8gxnb3p87h" />
            </button>
            <p className="text-[12.5px] font-medium text-ink-900" data-id="element-312" data-mp="1l6q8xlv5bygb">
              {monthNames[month]} {year}
            </p>
            <button type="button" onClick={() => step(1)} aria-label="Next month" className="rounded p-1 text-ink-500 hover:bg-ink-100" data-id="element-313" data-mp="1ukqlw4os9kzj">
              <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-314" data-mp="24aa4rachxeue" />
            </button>
          </div>

          <div className="mt-2 grid grid-cols-7 text-center text-[10.5px] text-ink-400" data-id="element-315" data-mp="1zdjq8rgcarb3">
            {dayLabels.map(d => <span key={d} className="py-1" data-id="element-316" data-mp="1bn7jvl0qt2mm">
                {d}
              </span>)}
          </div>

          <div className="grid grid-cols-7" data-id="element-317" data-mp="17np1ox0b35nh">
            {weeks.flat().map(date => {
          const outside = date.getMonth() !== month;
          const future = date > TODAY;
          const isToday = date.toDateString() === TODAY.toDateString();
          const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
          const selected = value === formatted;
          const disabled = future || outside;
          return <button key={date.toISOString()} type="button" disabled={disabled} onClick={() => {
            onChange(formatted);
            setOpen(false);
          }} className={`flex h-6 items-center justify-center rounded text-[11.5px] ${selected ? 'bg-brand-600 font-medium text-white' : disabled ? 'cursor-not-allowed text-ink-300' : 'text-ink-800 hover:bg-ink-100'} ${isToday && !selected ? 'bg-ink-100 font-medium' : ''}`} data-id="element-318" data-mp="11m6mxdgruuvu">
                  {date.getDate()}
                </button>;
        })}
          </div>
        </div>}
    </div>;
}